// ============================================================================
// EquipesPage — liste des equipes (E1b, F-206..F-212) — /equipes
// ============================================================================
// Portage du PoC (dashboard)/equipes/page.tsx (vue GESTION) au standard maison :
//   - garde d'entree SERVEUR (M-200/M-402) : GET /api/teams 403 pour un
//     non-management -> ecran de refus i18n ;
//   - grille de cartes PoC -> DataTable MAISON + drill-in par ligne (ecart
//     standard #2 « toute table offre un drill-in ») ;
//   - KPIs de gestion (F-206) : equipes, pulse moyen, pulses soumis, objectifs ;
//   - etats skeleton / empty / error (patterns E3a).
// Scope E1b : l'onglet « Forces & profils » (psychometrie agregee) et la vue
// identite employe du PoC ne sont PAS portes ici (STOP-question consignee au
// rapport — surface sensible + hors « A faire » du MASTER).

import { useNavigate } from 'react-router-dom';
import {
  PageHeader, EmptyState, StatusBadge, KpiCard, DataTable, GsProgressBar,
  SkeletonCard, QueryError, GS_COMPONENTS, type Column,
} from '@gs/ui';
import { Users, Smile, Activity, Target, ShieldAlert } from 'lucide-react';
import { ApiForbiddenError } from '@gs/api-client';
import { useAuth } from '@gs/auth';
import { useTranslation } from '../../i18n';
import { useTeams, type TeamSummary } from '../../hooks/useTeams';
import { monthShort } from './dates';

/** Ecran de refus management (M-200/M-402) — l'autorite est le 403 serveur. */
export function TeamsForbidden() {
  const { t } = useTranslation();
  return (
    <EmptyState
      icon={<ShieldAlert size={48} aria-hidden="true" />}
      title={t('gest:equipes.forbidden.title')}
      description={t('gest:equipes.forbidden.description')}
    />
  );
}

/** Moyenne pulse formatee locale (« 3,8 » / « 3.8 ») — 1 decimale. */
function formatMood(locale: string, value: number): string {
  return new Intl.NumberFormat(locale.startsWith('en') ? 'en-CA' : 'fr-CA', {
    minimumFractionDigits: 1, maximumFractionDigits: 1,
  }).format(value);
}

export default function EquipesPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();

  const listQ = useTeams(!!user);
  const forbidden = listQ.isError && listQ.error instanceof ApiForbiddenError;

  // ─── Colonnes DataTable (nom, responsable, statut, membres, etat pulse, objectifs) ──
  const columns: Column<TeamSummary>[] = [
    {
      key: 'name',
      header: t('gest:equipes.columns.name'),
      render: (team) => (
        <div className="min-w-0">
          <p className="truncate font-semibold text-slate-900 dark:text-slate-100">{team.name}</p>
          {team.department ? (
            <p className="truncate text-meta text-slate-500 dark:text-slate-400">{team.department}</p>
          ) : null}
        </div>
      ),
    },
    {
      key: 'director',
      header: t('gest:equipes.columns.director'),
      render: (team) =>
        team.directorName ?? team.directorEmail ?? (
          <span className="text-slate-400 dark:text-slate-500">{t('gest:equipes.noDirector')}</span>
        ),
    },
    {
      key: 'status',
      header: t('gest:equipes.columns.status'),
      render: (team) =>
        team.submittedThisPeriod ? (
          <StatusBadge variant="success">{t('gest:equipes.pulse.submitted')}</StatusBadge>
        ) : (
          <StatusBadge variant="neutral">{t('gest:equipes.pulse.pending')}</StatusBadge>
        ),
    },
    {
      key: 'members',
      header: t('gest:equipes.columns.members'),
      className: 'tabular-nums',
      render: (team) => team.memberCount || team.headcount,
    },
    {
      key: 'pulse',
      header: t('gest:equipes.columns.pulse'),
      render: (team) => {
        if (team.submittedThisPeriod && team.currentMood != null) {
          return <span className="font-semibold tabular-nums">{t('gest:equipes.pulse.score', { score: team.currentMood })}</span>;
        }
        if (team.latestMood != null && team.latestPeriod) {
          return (
            <span className="text-meta text-slate-500 dark:text-slate-400">
              {t('gest:equipes.pulse.lastSeen', {
                month: monthShort(i18n.language, team.latestPeriod),
                score: team.latestMood,
              })}
            </span>
          );
        }
        return <span className="text-slate-400 dark:text-slate-500">{t('gest:equipes.pulse.none')}</span>;
      },
    },
    {
      key: 'objectives',
      header: t('gest:equipes.columns.objectives'),
      render: (team) =>
        team.objectivesTotal > 0 ? (
          <GsProgressBar value={team.objectivesDone} max={team.objectivesTotal} label className="w-32" />
        ) : (
          <span className="text-slate-400 dark:text-slate-500">—</span>
        ),
    },
  ];

  return (
    <div className="space-y-5">
      <PageHeader title={t('gest:equipes.title')} />

      {forbidden ? (
        <TeamsForbidden />
      ) : listQ.isLoading ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
      ) : listQ.isError ? (
        <QueryError message={t('gest:equipes.loadError')} onRetry={() => void listQ.refetch()} />
      ) : listQ.data ? (
        <>
          {/* F-206 : KPIs de gestion — cards blanches, icone en pastille (jamais de card coloree). */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <KpiCard
              icon={Users}
              variant="brand"
              label={t('gest:equipes.kpis.teams')}
              value={t('gest:equipes.kpis.teamsValue', { teams: listQ.data.kpis.teamCount, people: listQ.data.kpis.totalHeadcount })}
            />
            <KpiCard
              icon={Smile}
              variant="green"
              label={t('gest:equipes.kpis.mood')}
              value={
                listQ.data.kpis.moodAvg != null
                  ? t('gest:equipes.pulse.score', { score: formatMood(i18n.language, listQ.data.kpis.moodAvg) })
                  : '—'
              }
            />
            <KpiCard
              icon={Activity}
              variant="blue"
              label={t('gest:equipes.kpis.submitted')}
              value={`${listQ.data.kpis.submittedCount}/${listQ.data.kpis.teamCount}`}
            />
            <KpiCard
              icon={Target}
              variant="brand"
              label={t('gest:equipes.kpis.objectives')}
              value={`${listQ.data.kpis.objectivesDone}/${listQ.data.kpis.objectivesTotal}`}
            />
          </div>

          {listQ.data.teams.length === 0 ? (
            <EmptyState icon={<Users size={48} aria-hidden="true" />} title={t('gest:equipes.empty')} />
          ) : (
            // DataTable maison + drill-in par ligne (ecart standard #2).
            <div className={`${GS_COMPONENTS.card} overflow-hidden p-0 dark:border-slate-700 dark:bg-slate-800`}>
              <DataTable
                columns={columns}
                data={listQ.data.teams}
                rowKey={(team) => team.id}
                onRowClick={(team) => navigate(`/equipes/${team.id}`)}
              />
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}
